import React from 'react';
import { ChartTooltip } from './ChartTooltip';
import { addOnChartColor, addOnIncomeDetails } from './addOnStats';
import { hasAddOnColor } from '../../util/addOns';
import {
  ResponsiveContainer,
  BarChart, Bar, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';

const fmtTooltipEuro = (v) => `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Up to this many bars stand upright ("Eintritt" + one add-on: the names below fit even at 375px); more bars —
// one per add-on — are drawn horizontally with the names on the left, like "Top 10 Anwesenheiten".
const MAX_UPRIGHT_BARS = 2;
const NAME_TICK_MAX = 16;

const shortName = (name) => {
  const text = String(name ?? '');
  return text.length > NAME_TICK_MAX ? `${text.slice(0, NAME_TICK_MAX - 1)}…` : text;
};

const axisStyle = (colors) => ({
  tick: { fill: colors.text, fontSize: 12, fontFamily: 'var(--font-mono)' },
  axisLine: false,
  tickLine: false,
});

// "Einnahmen-Breakdown": rows [{ name, Betrag (€), signupCents?, onSiteCents?, colorKey? }] — "Eintritt" + one bar
// per add-on. Bars use the positive gradient; an add-on with an own color (colorKey) gets a gradient in that color.
// The tooltip carries the full name and, for add-ons sold through both channels, the split.
function IncomeBreakdownChart({ data, colors, gradientId }) {
  const rows = Array.isArray(data) ? data : [];
  const horizontal = rows.length > MAX_UPRIGHT_BARS;

  // One gradient per color in use: the default (positive) + one per add-on color key.
  const colorKeys = [...new Set(rows.map(r => r.colorKey).filter(hasAddOnColor))];
  const keyGradientId = (key) => `${gradientId}-${key}`;
  const gradients = [
    { id: gradientId, color: colors.positive },
    ...colorKeys.map(key => ({ id: keyGradientId(key), color: addOnChartColor(colors, key) })),
  ];
  const fillOf = (row) => `url(#${hasAddOnColor(row.colorKey) ? keyGradientId(row.colorKey) : gradientId})`;

  const defs = (
    <defs>
      {gradients.map(g => (
        <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2={horizontal ? '1' : '0'} y2={horizontal ? '0' : '1'}>
          <stop offset="0%" stopColor={g.color} stopOpacity={0.9} />
          <stop offset="100%" stopColor={g.color} stopOpacity={0.5} />
        </linearGradient>
      ))}
    </defs>
  );

  const cells = rows.map((row, index) => <Cell key={`${row.name}-${index}`} fill={fillOf(row)} />);

  const tooltip = (
    <Tooltip
      isAnimationActive={false}
      content={({ active, payload, label }) => (
        <ChartTooltip active={active} payload={payload} label={label} formatValue={fmtTooltipEuro} details={addOnIncomeDetails} />
      )}
      cursor={{ fill: colors.borderMuted, opacity: 0.4 }}
    />
  );

  if (horizontal) {
    return (
      <ResponsiveContainer width="100%" height={Math.max(220, rows.length * 34)}>
        <BarChart data={rows} layout="vertical" margin={{ left: 8 }}>
          {defs}
          <CartesianGrid stroke={colors.borderMuted} strokeDasharray="4 4" horizontal={false} />
          <XAxis type="number" {...axisStyle(colors)} tickFormatter={(v) => `${v}€`} />
          <YAxis type="category" dataKey="name" {...axisStyle(colors)} width={124} interval={0} tickFormatter={shortName} />
          {tooltip}
          <Bar dataKey="Betrag" fill={`url(#${gradientId})`} radius={[0, 3, 3, 0]} maxBarSize={22}>
            {cells}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={rows} barCategoryGap="40%">
        {defs}
        <CartesianGrid stroke={colors.borderMuted} strokeDasharray="4 4" vertical={false} />
        <XAxis dataKey="name" {...axisStyle(colors)} interval={0} tickFormatter={shortName} />
        <YAxis {...axisStyle(colors)} tickFormatter={(v) => `${v}€`} width={64} />
        {tooltip}
        <Bar dataKey="Betrag" fill={`url(#${gradientId})`} radius={[3, 3, 0, 0]} maxBarSize={80}>
          {cells}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default IncomeBreakdownChart;
